import React, { useState } from 'react';
import styled from 'styled-components';

import {
  AnimateIn,
  Card,
  Container,
  Grid,
  GridItem,
  Heading,
  InternalLink,
  TitleSet,
  animation,
  colors,
  fontSizes
} from '../ui-components';

import { GatsbyImage } from 'gatsby-plugin-image';
import renderRichText from '../utils/render-rich-text';
import Icon from './icon';

/**
 * Renders the Internal Link Section
 *
 * @function renderInternalLinkSection
 * 
 * @prop {object} povContent
 * @prop {object} children
 * @prop {boolean} renderIcon
 * @prop {string} pageType
 * @prop {string} space
 * 
 * @returns {JSX}
 */
const InternalLinkSection = props => {
  const {povContent, children, renderIcon = false, pageType, space} = props;
  const [iconRngSeed] = useState(Date.now()); // using useState hook to set seed once for all component lifecycle

  let label = props?.label;
  let title = props?.title;
  let content = props?.content;
  let blocks = props?.blocks;
  let staticCardImages = props?.staticCardImages;

  if (povContent) {
    label = povContent?.label ? povContent.label : label;
    title = povContent?.title ? povContent.title : title;
    content = povContent?.bodyRichJSON ? povContent.bodyRichJSON : content;
    blocks = povContent?.items ? povContent.items : blocks;
    staticCardImages = povContent?.images ? povContent.images : staticCardImages;
  }

  let layout = 'noIcon';
  let subtitle = content?.raw ? renderRichText(content, space) : content;

  const renderCardContents = block => {
    return (
      <>
        {block?.image ? (
          <CardHero image={block?.image?.data || block?.image} alt={`${block?.image?.title || block?.title} image`} />
        ) : (
          staticCardImages[block?.title] && <CardHeroImg src={staticCardImages[block?.title]} alt={block?.title} />
        )}
        <CardText>
          <Heading level={5}>{block.title}</Heading>
          <InternalLinkTextSubtitleContainer className={layout}>
            {<p>{block?.content?.raw ? renderRichText(block.content, space) : block?.content}</p>}
          </InternalLinkTextSubtitleContainer>
        </CardText>
      </>
    );
  };

  const linkBlocks = blocks?.map(block => {
    return (
      <GridItem col={4} collg={6} colsm={12} key={block.title}>
        <CardContainer className={layout} href={block.slug}>
          {block?.slug ? (
            <WrapperLink to={`/${block.slug}`}>{renderCardContents(block)}</WrapperLink>
          ) : (
            <WrapperContainer>{renderCardContents(block)}</WrapperContainer>
          )}
        </CardContainer>
      </GridItem>
    );
  });

  return (
    <AnimateIn onScroll>
      <InternalLinkSectionContainer id="internal-link-section" sectionContainer>
        {renderIcon && <Icon horizontal={'left'} vertical={'top'} pageType={pageType} iconRngSeed={iconRngSeed}/>}
        <TitleSet label={label} title={title} bodyText={subtitle} />
        <Grid gridGap={24}>{linkBlocks}</Grid>
        {children}
      </InternalLinkSectionContainer>
    </AnimateIn>
  );
};

const InternalLinkSectionContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
`;

const CardContainer = styled(Card)`
  display: flex;
  gap: 0;
  width: 100%;
  min-height: 400px;

  &:hover.noIcon {
    background-color: ${colors.cardBackgroundHover};

    h5,
    p,
    a {
      color: ${colors.pureWhite};
    }
  }
`;

const WrapperLink = styled(props => <InternalLink {...props} />)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  text-transform: none;
  width: 100%;
  overflow: hidden;
  align-items: inherit;
`;

const WrapperContainer = styled(props => <div {...props} />)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  text-transform: none;
  width: 100%;
  overflow: hidden;
  align-items: inherit;
`;

const CardHero = styled(GatsbyImage)`
  padding: 0;
  margin: -2px 0 0;
  height: 280px;

  transition: transform ${animation.general.durationAndTiming};

  ${WrapperLink}:hover & {
    transform: scale(1.035);
  }
`;

const CardHeroImg = styled.img`
  padding: 0;
  margin: -2px 0 0;
  height: 280px;
  object-fit: cover;

  transition: transform ${animation.general.durationAndTiming};

  ${WrapperLink}:hover & {
    transform: scale(1.035);
  }
`;

const CardText = styled.div`
  display: flex;
  padding: 28px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const InternalLinkTextSubtitleContainer = styled(Container)`
  font-family: Late, sans-serif;
  font-size: ${fontSizes.paragraph.medium.fontSize};
  margin-top: 0;
  padding: 0;

  p {
    margin: 0;
    color: black;
    font-family: 'Lato', sans-serif;
    font-size: ${fontSizes.paragraph.medium.fontSize};
    font-weight: 400;
    line-height: 26px;
  }
`;

export default InternalLinkSection;
